import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useJsonLd, getSiteUrl } from "@/hooks/useJsonLd";

export default function ArticleJakKorzystac() {
  const navigate = useNavigate();
  const siteUrl = getSiteUrl();
  useEffect(() => { window.scrollTo(0, 0); document.title = "Jak Korzystać z Kakobuy Spreadsheet – Poradnik Krok po Kroku | Kakobuy Spreadsheet"; }, []);

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Jak Korzystać z Kakobuy Spreadsheet – Poradnik Krok po Kroku",
    "description": "Praktyczny poradnik krok po kroku dla początkujących — naucz się efektywnie korzystać z arkusza Kakobuy Spreadsheet.",
    "url": `${siteUrl}/artykul/jak-korzystac-z-kakobuy-spreadsheet`,
    "datePublished": "2026-06-21",
    "dateModified": "2026-06-21",
    "inLanguage": "pl",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "image": "https://readdy.ai/api/search-image?query=Person%20typing%20on%20laptop%20with%20shopping%20list%20and%20fashion%20magazine%20on%20light%20terracotta%20desk%2C%20warm%20studio%20light%2C%20editorial%20photography%2C%20organized%20workspace%2C%20modern%20European%20creative%20aesthetic&width=1600&height=900&seq=kako-art-02-hero&orientation=landscape",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/artykul/jak-korzystac-z-kakobuy-spreadsheet`,
    },
  });

  return (
    <>
      <Navbar />
      <main>
        <section className="relative min-h-[55vh] flex flex-col overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://readdy.ai/api/search-image?query=Person%20typing%20on%20laptop%20with%20shopping%20list%20and%20fashion%20magazine%20on%20light%20terracotta%20desk%2C%20warm%20studio%20light%2C%20editorial%20photography%2C%20organized%20workspace%2C%20modern%20European%20creative%20aesthetic&width=1600&height=900&seq=kako-art-02-hero&orientation=landscape)" }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/35"></div>
          <div className="relative z-10 flex-1 flex items-center pt-24 pb-12">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <span className="inline-block px-4 py-1.5 bg-background-50/60 backdrop-blur-md rounded-full text-xs font-semibold text-foreground-800 mb-6 whitespace-nowrap">Blog &gt; Poradnik</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">Jak Korzystać<br />z Kakobuy<br />Spreadsheet?</h1>
              <p className="text-base text-background-50/85 mt-4 max-w-2xl">Praktyczny poradnik krok po kroku dla początkujących — naucz się efektywnie korzystać z arkusza Kakobuy Spreadsheet.</p>
            </div>
          </div>
        </section>

        <article className="py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="prose prose-lg max-w-none">
              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">Krok po kroku — jak zacząć z <span className="italic text-foreground-600 font-normal">Kakobuy Spreadsheet</span></h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-5 bg-background-100 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-foreground-900 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                    <div><h4 className="font-semibold text-foreground-900 mb-1">Wejdź na stronę główną</h4><p className="text-sm text-foreground-600">Odwiedź <a href="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer" onClick={(e) => { e.preventDefault(); navigate("/"); }}>kakobuyspreadsheet.my</a> i zapoznaj się z kategoriami produktów.</p></div>
                  </div>
                  <div className="flex items-start gap-4 p-5 bg-background-100 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-foreground-900 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                    <div><h4 className="font-semibold text-foreground-900 mb-1">Wybierz kategorię</h4><p className="text-sm text-foreground-600">Skorzystaj z rozwijanego menu lub kliknij na kartę kategorii, która Cię interesuje.</p></div>
                  </div>
                  <div className="flex items-start gap-4 p-5 bg-background-100 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-foreground-900 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                    <div><h4 className="font-semibold text-foreground-900 mb-1">Przejdź do arkusza</h4><p className="text-sm text-foreground-600">Kliknij przycisk "Kakobuy Spreadsheet", aby otworzyć pełny arkusz na <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">kaspreadsheet.com</a>.</p></div>
                  </div>
                  <div className="flex items-start gap-4 p-5 bg-background-100 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-foreground-900 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">4</div>
                    <div><h4 className="font-semibold text-foreground-900 mb-1">Wybierz produkt i zamów</h4><p className="text-sm text-foreground-600">Kliknij w link do produktu, dodaj do koszyka w Kakobuy i dokończ zamówienie.</p></div>
                  </div>
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">Wskazówki dla początkujących</h2>
                <ul className="space-y-3 text-foreground-700">
                  <li className="flex items-start gap-2"><i className="ri-lightbulb-line text-accent-600 mt-0.5 flex-shrink-0"></i>Zawsze sprawdzaj tabelę rozmiarów przed zakupem odzieży i butów</li>
                  <li className="flex items-start gap-2"><i className="ri-lightbulb-line text-accent-600 mt-0.5 flex-shrink-0"></i>Pierwsze zamówienie zrób na małą kwotę, aby przetestować proces</li>
                  <li className="flex items-start gap-2"><i className="ri-lightbulb-line text-accent-600 mt-0.5 flex-shrink-0"></i>Korzystaj z opcji śledzenia przesyłki — to darmowa funkcja Kakobuy</li>
                  <li className="flex items-start gap-2"><i className="ri-lightbulb-line text-accent-600 mt-0.5 flex-shrink-0"></i>Zapisz link do arkusza w zakładkach, aby mieć szybki dostęp</li>
                </ul>
              </section>

              <section className="text-center py-10">
                <p className="text-lg text-foreground-700 mb-6">Gotowy, aby zacząć? Sprawdź <strong>Kakobuy Spreadsheet</strong> już teraz.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="bg-foreground-900 text-background-50 px-7 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <a href="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer" onClick={(e) => { e.preventDefault(); navigate("/"); }}><i className="ri-arrow-left-line mr-1"></i>Strona główna</a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}